import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";
import axios from "axios";
import CONFIG from "../config";

const UserDataView = ({
  searchQuery,
  setSearchQuery,
  selectedCompany: propSelectedCompany,
  onCompanySelect
}) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [periodType, setPeriodType] = useState("Annual");
  const [activeStatement, setActiveStatement] = useState(null);
  const [financialData, setFinancialData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [allUserCompanies, setAllUserCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  // Get auth headers using CONFIG helper
  const getAuthHeaders = () => CONFIG.getAuthHeaders();

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    // Load user's accessible companies on component mount
    loadUserCompanies();
    
    // If we have a prop selected company (from search), use it
    if (propSelectedCompany) {
      setSelectedCompany(propSelectedCompany);
      setShowSearchResults(false);
      // Set default statement
      const defaultStatement = propSelectedCompany.hasIncomeStatement ? "Income Statement" :
                              propSelectedCompany.hasBalanceSheet ? "Balance Sheet" :
                              propSelectedCompany.hasCashFlow ? "Cash Flow" : null;
      if (defaultStatement) {
        setActiveStatement(defaultStatement);
      }
    }
  }, [propSelectedCompany]);

  /* ---------------- SEARCH EFFECT ---------------- */
  useEffect(() => {
    if (!searchQuery.trim()) {
      setShowSearchResults(false);
      setFilteredCompanies([]);
      return;
    }

    const searchCompanies = () => {
      const cleanQuery = searchQuery.toLowerCase().trim();
      
      if (cleanQuery.length === 0) {
        setFilteredCompanies([]);
        setShowSearchResults(false);
        return;
      }

      // Filter from all user companies
      const filtered = allUserCompanies.filter(company => {
        const name = company.name?.toLowerCase() || '';
        const ticker = company.ticker?.toLowerCase() || '';
        const sector = company.sector?.toLowerCase() || '';
        
        return name.includes(cleanQuery) || 
               ticker.includes(cleanQuery) || 
               sector.includes(cleanQuery);
      });

      console.log('Search results:', {
        query: cleanQuery,
        allCompanies: allUserCompanies.length,
        filtered: filtered.length,
        filteredCompanies: filtered
      });

      setFilteredCompanies(filtered);
      setShowSearchResults(filtered.length > 0);
    };

    // Debounce search
    const timer = setTimeout(searchCompanies, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, allUserCompanies]);

  /* ---------------- NOTIFY PARENT ABOUT STATEMENT CHANGE ---------------- */
  useEffect(() => {
    if (selectedCompany && activeStatement) {
      // Dispatch event to notify parent component about statement change
      window.dispatchEvent(new CustomEvent('userDataViewStatementChanged', {
        detail: {
          statementType: activeStatement,
          company: selectedCompany
        }
      }));
      
      // Also update the global download function
      window.handleUserDataDownload = handleExcelDownload;
    }
  }, [activeStatement, selectedCompany]);

  /* ---------------- LOAD USER'S COMPANIES ---------------- */
  const loadUserCompanies = async () => {
    try {
      setIsLoadingCompanies(true);
      console.log('Loading user accessible companies...');
      
      // Use CONFIG helper to get full URL
      const searchUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.SEARCH);
      console.log('Search URL:', searchUrl);
      
      const response = await axios.get(
        searchUrl,
        {
          headers: getAuthHeaders(),
          params: {
            page: 1,
            page_size: 100  // Get all accessible companies
          }
        }
      );
      
      console.log('User companies response:', response.data);
      
      // Handle different response structures
      let companiesData = [];
      
      if (response.data && response.data.files) {
        companiesData = response.data.files;
        console.log('Found files in response.data.files:', companiesData.length);
      } else if (Array.isArray(response.data)) {
        companiesData = response.data;
        console.log('Response.data is an array:', companiesData.length);
      } else if (response.data && response.data.companies) {
        companiesData = response.data.companies;
        console.log('Found companies in response.data.companies:', companiesData.length);
      }
      
      if (companiesData.length > 0) {
        console.log('Companies data to transform:', companiesData);
        
        const companies = companiesData.map((file, index) => {
          const companyName = file.company_name || file.name || `Company ${index + 1}`;
          return {
            id: file.company_id || file.id || `temp-${Date.now()}-${index}`,
            name: companyName,
            ticker: file.ticker || file.ticker_id || companyName.substring(0, 3).toUpperCase(),
            sector: file.sector || "Financial Services",
            isBackendCompany: true,
            hasIncomeStatement: file.hasIncomeStatement || false,
            hasBalanceSheet: file.hasBalanceSheet || false,
            hasCashFlow: file.hasCashFlow || false,
            companyId: file.company_id || file.id,
            latestYear: file.latest_year,
            downloadUrls: file.download_urls,
            statement_data: file.statement_data
          };
        });
        
        console.log('Transformed companies:', companies);
        setAllUserCompanies(companies);
        console.log(`Loaded ${companies.length} companies for user`);
      } else {
        console.log('No companies found for user');
        setAllUserCompanies([]);
      }
    } catch (error) {
      console.error('Error loading user companies:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setAllUserCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  /* ---------------- LOAD STATEMENT DATA ---------------- */
  const loadStatementData = async (companyName, statementType) => {
    if (!companyName || !statementType) {
      setFinancialData([]);
      return;
    }
    
    setIsLoading(true);
    setFinancialData([]);
    
    try {
      console.log(`Loading ${statementType} data for ${companyName}`);
      
      // Search for the company to get statement IDs
      const searchUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.SEARCH);
      const searchResponse = await axios.get(
        searchUrl,
        {
          headers: getAuthHeaders(),
          params: {
            company_name: companyName,
            page: 1,
            page_size: 10
          }
        }
      );
      
      console.log(`Search response for ${companyName}:`, searchResponse.data);
      
      if (searchResponse.data.files && searchResponse.data.files.length > 0) {
        const companyData = searchResponse.data.files[0];
        
        // Map frontend statement type to backend type
        const statementTypeMap = {
          "Income Statement": "income_statement",
          "Balance Sheet": "balance_sheet", 
          "Cash Flow": "cash_flow"
        };
        
        const backendType = statementTypeMap[statementType];
        const statementInfo = companyData.statement_data?.[backendType];
        
        console.log(`Statement info for ${statementType}:`, statementInfo);
        
        if (statementInfo && statementInfo.statement_id) {
          // Get Excel data from backend
          const dataUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.STATEMENT_DATA(statementInfo.statement_id));
          const dataResponse = await axios.get(
            dataUrl,
            {
              headers: getAuthHeaders()
            }
          );
          
          console.log(`Backend data response for ${statementType}:`, dataResponse.data);
          
          if (dataResponse.data.success && dataResponse.data.data) {
            const jsonData = dataResponse.data.data;
            
            // Format the data for display
            const formattedData = formatDataForTable(jsonData);
            setFinancialData(formattedData);
            
            // Extract years from data
            const years = extractYearsFromData(formattedData);
            setAvailableYears(years);
            
            console.log(`✅ Successfully loaded ${formattedData.length} rows for ${statementType}`);
          } else {
            console.log(`❌ No data in backend response for ${statementType}`);
            setFinancialData([]);
            setAvailableYears([]);
          }
        } else {
          console.log(`❌ No statement ID found for ${statementType}`);
          setFinancialData([]);
          setAvailableYears([]);
        }
      } else {
        console.log(`❌ No company found: ${companyName}`);
        setFinancialData([]);
        setAvailableYears([]);
      }
    } catch (error) {
      console.error(`❌ Error loading ${statementType} data:`, error);
      setFinancialData([]);
      setAvailableYears([]);
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- FORMAT DATA FOR TABLE ---------------- */
  const formatDataForTable = (jsonData) => {
    if (!jsonData || !Array.isArray(jsonData) || jsonData.length === 0) {
      return [];
    }
    
    console.log("Raw data received:", jsonData);
    
    // Filter out rows that are completely empty or have only "Particular" or "Unnamed:" headers
    const filteredData = jsonData.filter(row => {
      // Check if row has any non-empty values
      const hasValues = Object.values(row).some(value => 
        value !== null && value !== undefined && value !== ''
      );
      
      // Check first cell specifically
      const firstCell = row[Object.keys(row)[0]];
      const isHeaderRow = firstCell === "Particular" || 
                         (firstCell && firstCell.toString().startsWith("Unnamed:"));
      
      return hasValues && !isHeaderRow;
    });
    
    console.log("Filtered data:", filteredData);
    
    if (filteredData.length === 0) {
      return [];
    }
    
    // Check if the first row contains "CASH FLOW STATEMENT" or similar
    const firstRow = filteredData[0];
    const firstCellValue = firstRow[Object.keys(firstRow)[0]];
    
    // If first row is the statement title (contains "STATEMENT"), we need to handle it specially
    const isFirstRowStatementTitle = firstCellValue && 
                                    typeof firstCellValue === 'string' && 
                                    firstCellValue.toLowerCase().includes('statement');
    
    let processedData = [];
    
    if (isFirstRowStatementTitle) {
      // The data is already in correct format - first row is statement title
      // Second row should be Description/FY headers
      processedData = filteredData;
    } else {
      // Check if we need to insert a statement title row
      // Look for a row that might contain "Description" as first column
      const descriptionRowIndex = filteredData.findIndex(row => {
        const firstCell = row[Object.keys(row)[0]];
        return firstCell && 
               typeof firstCell === 'string' && 
               firstCell.toLowerCase().includes('description');
      });
      
      if (descriptionRowIndex > 0) {
        // The row before Description row might be the statement title
        const potentialTitleRow = filteredData[descriptionRowIndex - 1];
        const potentialTitleCell = potentialTitleRow[Object.keys(potentialTitleRow)[0]];
        
        if (potentialTitleCell && typeof potentialTitleCell === 'string') {
          // Check if it's a valid title (not empty and not a year)
          const isPotentialTitle = potentialTitleCell.trim() !== '' && 
                                  !potentialTitleCell.match(/\b(19|20)\d{2}\b/) &&
                                  !potentialTitleCell.toLowerCase().includes('activities') &&
                                  !potentialTitleCell.toLowerCase().includes('operating');
          
          if (isPotentialTitle) {
            // Reconstruct the data with proper structure
            processedData = filteredData;
          } else {
            // Insert a statement title row
            const statementTitleRow = {};
            const keys = Object.keys(filteredData[0]);
            keys.forEach((key, index) => {
              if (index === 0) {
                statementTitleRow[key] = `${activeStatement}`.toUpperCase();
              } else {
                statementTitleRow[key] = '';
              }
            });
            processedData = [statementTitleRow, ...filteredData];
          }
        } else {
          processedData = filteredData;
        }
      } else {
        processedData = filteredData;
      }
    }
    
    console.log("Processed data structure:", processedData);
    
    // Format the data
    const formattedData = processedData.map(row => {
      const formattedRow = {};
      
      Object.keys(row).forEach(key => {
        let value = row[key];
        
        // Handle null/undefined
        if (value === null || value === undefined) {
          value = '';
        }
        
        // Convert to string for consistent handling
        const stringValue = String(value);
        
        // Check if it's a number and format it
        if (key !== 'Particular' && key !== 'particular' && key !== 'Description' && key !== 'description' && stringValue !== '') {
          // Remove commas, currency symbols, etc.
          const cleaned = stringValue.replace(/[$,%\s()]/g, '');
          
          // Check if it's a number
          if (cleaned !== '' && !isNaN(cleaned)) {
            const numValue = parseFloat(cleaned);
            
            // Check if original had parentheses (negative)
            if (stringValue.includes('(') && stringValue.includes(')')) {
              value = -Math.abs(numValue);
            } else {
              value = numValue;
            }
          }
        }
        
        formattedRow[key] = value;
      });
      
      return formattedRow;
    });
    
    console.log("Final formatted data:", formattedData);
    
    return formattedData;
  };

  /* ---------------- EXTRACT YEARS FROM DATA ---------------- */
  const extractYearsFromData = (data) => {
    if (!data || data.length === 0) return [];
    
    const years = new Set();
    
    // Look for year patterns in the header row (row 1)
    if (data.length > 1) {
      const headerRow = data[1];
      if (headerRow) {
        Object.keys(headerRow).forEach(key => {
          if (key !== Object.keys(headerRow)[0]) { // Skip first column
            const value = headerRow[key];
            if (value && typeof value === 'string') {
              const yearMatch = value.match(/\b(19|20)\d{2}\b/);
              if (yearMatch) {
                years.add(yearMatch[0]);
              }
            }
          }
        });
      }
    }
    
    return Array.from(years).sort();
  };

  /* ---------------- HANDLE STATEMENT CHANGE ---------------- */
  useEffect(() => {
    if (selectedCompany && activeStatement) {
      loadStatementData(selectedCompany.name, activeStatement);
    }
  }, [activeStatement, selectedCompany]);

  /* ---------------- SELECT COMPANY ---------------- */
  const handleSelectCompany = (company) => {
    console.log('Selecting company:', company);
    setSelectedCompany(company);
    setSearchQuery(`${company.name} (${company.ticker})`);
    setShowSearchResults(false);
    
    // Set default statement and load data
    const defaultStatement = company.hasIncomeStatement ? "Income Statement" :
                           company.hasBalanceSheet ? "Balance Sheet" :
                           company.hasCashFlow ? "Cash Flow" : null;
    
    if (defaultStatement) {
      setActiveStatement(defaultStatement);
    } else {
      // If no specific statement has data, default to Income Statement
      setActiveStatement("Income Statement");
    }
    
    if (onCompanySelect) {
      onCompanySelect(company);
    }
  };

  /* ---------------- EXCEL DOWNLOAD ---------------- */
  const handleExcelDownload = async () => {
    if (!selectedCompany || !activeStatement || !selectedCompany.isBackendCompany) {
      alert("Please select a backend company and wait for data to load.");
      return;
    }

    try {
      console.log('Starting download process...', {
        company: selectedCompany.name,
        statement: activeStatement,
        CONFIG: CONFIG // Log CONFIG for debugging
      });

      // Get the statement ID for download
      const searchUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.SEARCH);
      console.log('Search URL for statement ID:', searchUrl);
      
      const searchResponse = await axios.get(
        searchUrl,
        {
          headers: getAuthHeaders(),
          params: {
            company_name: selectedCompany.name,
            page: 1,
            page_size: 10
          }
        }
      );
      
      console.log('Search response:', searchResponse.data);
      
      if (searchResponse.data.files && searchResponse.data.files.length > 0) {
        const companyData = searchResponse.data.files[0];
        console.log('Company data found:', companyData);
        
        const statementTypeMap = {
          "Income Statement": "income_statement",
          "Balance Sheet": "balance_sheet", 
          "Cash Flow": "cash_flow"
        };
        
        const backendType = statementTypeMap[activeStatement];
        console.log('Backend type:', backendType);
        
        const statementInfo = companyData.statement_data?.[backendType];
        console.log('Statement info:', statementInfo);
        
        if (statementInfo && statementInfo.statement_id && statementInfo.job_id) {
          console.log('Found statement ID:', statementInfo.statement_id);
          
          // FIX: Handle CONFIG.API.EXCEL.STATEMENT_DOWNLOAD properly
          let downloadUrl;
          
          // Check if STATEMENT_DOWNLOAD is a function
          if (typeof CONFIG.API.EXCEL.STATEMENT_DOWNLOAD === 'function') {
            downloadUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.STATEMENT_DOWNLOAD(statementInfo.statement_id));
          } 
          // Check if it's a string template
          else if (typeof CONFIG.API.EXCEL.STATEMENT_DOWNLOAD === 'string') {
            downloadUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.STATEMENT_DOWNLOAD.replace('{statementId}', statementInfo.statement_id));
          }
          // If not defined, use default pattern
          else {
            downloadUrl = CONFIG.getFullUrl(`/excel/statement/${statementInfo.statement_id}/download`);
          }
          
          console.log('Constructed download URL:', downloadUrl);
          
          // Also try alternative URLs
          const alternativeUrls = [
            downloadUrl,
            CONFIG.getFullUrl(`/upload/jobs/${statementInfo.job_id}/download/${statementInfo.statement_id}`),
            CONFIG.getFullUrl(`/excel/statement/${statementInfo.statement_id}/download`),
            `/api/v1/excel/statement/${statementInfo.statement_id}/download`
          ];
          
          console.log('Alternative URLs to try:', alternativeUrls);
          
          let downloadSuccess = false;
          let lastError = null;
          
          // Try each URL
          for (const url of alternativeUrls) {
            try {
              console.log(`Trying download URL: ${url}`);
              
              const response = await axios.get(
                url,
                {
                  headers: getAuthHeaders(),
                  responseType: 'blob',
                  timeout: 30000
                }
              );
              
              console.log('Download response received:', {
                status: response.status,
                size: response.data.size,
                type: response.data.type
              });
              
              if (response.data && response.data.size > 0) {
                // Create download link
                const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = blobUrl;
                
                // Create filename
                const cleanCompanyName = selectedCompany.name
                  .replace(/[^a-zA-Z0-9\s\-_]/g, '')
                  .replace(/\s+/g, '_');
                const cleanStatementType = activeStatement.replace(/\s+/g, '_');
                const filename = `${cleanCompanyName}_${cleanStatementType}.xlsx`;
                
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                
                // Cleanup
                setTimeout(() => {
                  link.remove();
                  window.URL.revokeObjectURL(blobUrl);
                }, 100);
                
                console.log(`✅ ${activeStatement} downloaded: ${filename}`);
                downloadSuccess = true;
                break;
              }
            } catch (error) {
              console.warn(`URL ${url} failed:`, error.message);
              lastError = error;
            }
          }
          
          if (!downloadSuccess) {
            throw new Error(lastError?.message || 'All download attempts failed');
          }
        } else {
          alert(`No downloadable file found for ${activeStatement}. Please check if the file exists on the server.`);
        }
      } else {
        alert("Company data not found. Please try selecting the company again.");
      }
    } catch (error) {
      console.error('Download error:', error);
      
      let errorMessage = 'Failed to download Excel file. ';
      if (error.response?.status === 404) {
        errorMessage = 'File not found on server.';
      } else if (error.response?.status === 403) {
        errorMessage = 'No permission to download this file.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Please login again.';
      }
      
      alert(errorMessage);
    }
  };

  /* ---------------- FORMAT VALUE FOR DISPLAY ---------------- */
  const formatValue = (val, columnId, rowIndex) => {
    if (val === "" || val == null) return "";
    
    // First row (statement title) and second row (headers) - return as is
    if (rowIndex === 0 || rowIndex === 1) {
      return val;
    }
    
    // First column is always text
    const firstColumnId = financialData.length > 0 ? Object.keys(financialData[0])[0] : "";
    if (columnId === firstColumnId) {
      return val;
    }
    
    // Handle numeric formatting for other columns
    if (typeof val === "string") {
      // Check if it's already formatted with parentheses
      if (val.includes("%") || val.startsWith("$") || val.startsWith("($") || val.startsWith(" -")) {
        return val;
      }
      
      // Handle negative numbers without parentheses
      if (val.startsWith("-") && !val.includes("(")) {
        const numStr = val.replace(/[^0-9.]/g, '');
        const num = parseFloat(numStr);
        if (!isNaN(num)) {
          return `(${Math.abs(num).toLocaleString("en-US")})`;
        }
      }
    }
    
    const num = Number(val);
    if (isNaN(num)) return val;
    
    if (num < 0) {
      return `(${Math.abs(num).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })})`;
    }
    
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  /* ---------------- GET COLUMNS FROM DATA ---------------- */
  const getColumns = () => {
    if (financialData.length === 0) {
      return [];
    }
    
    // Get column keys from the first data row
    const columnKeys = Object.keys(financialData[0]);
    
    // Create column definitions
    return columnKeys.map((key, index) => {
      let width = 120;
      
      // For first column
      if (index === 0) {
        width = 180;
      }
      
      return {
        id: key,
        width: width
      };
    });
  };

  /* ---------------- HANDLE STATEMENT TAB CLICK ---------------- */
  const handleStatementTabClick = (statement) => {
    setActiveStatement(statement);
  };

  // Get table columns
  const tableColumns = getColumns();

  // Render the table
  return (
    <div className="h-screen bg-white dark:bg-gray-800">
      {/* COMPANY HEADER */}
      {selectedCompany && (
        <div className="px-6 pt-3 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {selectedCompany.name}{" "}
                <span className="text-blue-600 dark:text-blue-400">
                  ({selectedCompany.ticker})
                </span>
              </h2>

              {/* Period Toggle */}
              {availableYears.length > 0 && (
                <div className="inline-flex border rounded-md overflow-hidden border-gray-300 dark:border-gray-600">
                  {["Annual", "Quarterly"].map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriodType(p)}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${periodType === p
                          ? "bg-blue-600 dark:bg-blue-700 text-white"
                          : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                        }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SEARCH RESULTS DROPDOWN */}
      {showSearchResults && searchQuery && filteredCompanies.length > 0 && (
        <div className="absolute z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg
                        w-96 max-h-60 overflow-y-auto shadow-lg mt-1 ml-6">
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {filteredCompanies.length} company{filteredCompanies.length !== 1 ? 's' : ''} found
              {isLoadingCompanies && " (loading...)"}
            </p>
          </div>
          {filteredCompanies.map(c => (
            <div
              key={c.id}
              onClick={() => handleSelectCompany(c)}
              className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700
                       cursor-pointer text-sm border-b border-gray-200 dark:border-gray-600 last:border-b-0"
            >
              <div className="font-medium text-gray-900 dark:text-white">{c.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {c.ticker} • {c.sector}
                {c.hasIncomeStatement && <span className="ml-2 text-green-600">Income ✓</span>}
                {c.hasBalanceSheet && <span className="ml-2 text-blue-600">Balance ✓</span>}
                {c.hasCashFlow && <span className="ml-2 text-purple-600">Cash ✓</span>}
              </div>
            </div>
          ))}
        </div>
      )}

     

      {/* STATEMENT TABS */}
      {selectedCompany && (
        <div className="px-6 mt-2">
          <div className="inline-flex gap-1 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-sm">
            {["Income Statement", "Balance Sheet", "Cash Flow"].map((s) => (
              <button
                key={s}
                onClick={() => handleStatementTabClick(s)}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${activeStatement === s
                    ? "bg-white dark:bg-gray-700 text-yellow-700 dark:text-yellow-400 shadow-sm border border-yellow-300 dark:border-yellow-600"
                    : "bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/50"
                  }`}
              >
                {s}
                {selectedCompany.isBackendCompany && (
                  <>
                    {s === "Income Statement" && selectedCompany.hasIncomeStatement && (
                      <span className="ml-2 text-xs text-green-600 dark:text-green-400">✓</span>
                    )}
                    {s === "Balance Sheet" && selectedCompany.hasBalanceSheet && (
                      <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">✓</span>
                    )}
                    {s === "Cash Flow" && selectedCompany.hasCashFlow && (
                      <span className="ml-2 text-xs text-purple-600 dark:text-purple-400">✓</span>
                    )}
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* DATA TABLE */}
      {selectedCompany && activeStatement && (
        <div className="h-[calc(100vh-170px)] overflow-auto px-6 mt-2">
          {isLoading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 dark:border-blue-400" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">
                Loading {activeStatement.toLowerCase()} data...
              </span>
            </div>
          ) : financialData.length > 0 ? (
            <div className="excel-wrapper">
              <table className="excel-table">
                <tbody>
                  {financialData.map((row, rowIndex) => {
                    const isAlternateRow = rowIndex > 1 && rowIndex % 2 === 0; // Alternate from row 2 onwards
                    const firstCellValue = row[tableColumns[0]?.id] || "";
                    
                    // Check if this is the statement title row (Row 0)
                    const isStatementTitle = rowIndex === 0 && 
                                            typeof firstCellValue === 'string' && 
                                            firstCellValue.toLowerCase().includes('statement');
                    
                    // Check if this is the header row (Row 1)
                    const isHeaderRow = rowIndex === 1;
                    
                    // STATEMENT TITLE ROW (Row 0) - Centered, Bold, spans all columns
                    if (isStatementTitle) {
                      return (
                        <tr key={rowIndex} className="excel-title-row">
                          <td
                            colSpan={tableColumns.length}
                            className="excel-title-cell"
                            style={{
                              textAlign: 'center',
                              fontWeight: 'bold',
                              fontSize: '13px',
                              height: '30px',
                              padding: '4px 6px'
                            }}
                          >
                            {firstCellValue}
                          </td>
                        </tr>
                      );
                    }
                    
                    // HEADER ROW (Row 1) - Bold, first column left-aligned, others centered
                    if (isHeaderRow) {
                      return (
                        <tr key={rowIndex} className="excel-header-row">
                          {tableColumns.map((col, colIndex) => {
                            const value = row[col.id] || "";
                            const isFirstColumn = colIndex === 0;
                            
                            return (
                              <td
                                key={col.id}
                                className="excel-header-cell"
                                style={{
                                  textAlign: isFirstColumn ? 'left' : 'center',
                                  fontWeight: 'bold',
                                  fontSize: '12px',
                                  height: '28px',
                                  padding: '4px 6px'
                                }}
                              >
                                {value}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    }
                    
                    // DATA ROWS (Row 2 onwards) - Normal font weight
                    return (
                      <tr
                        key={rowIndex}
                        className="excel-data-row"
                        style={{
                          backgroundColor: isAlternateRow ? '#f0f8ff' : '#ffffff'
                        }}
                      >
                        {tableColumns.map((col, colIndex) => {
                          const isFirstColumn = colIndex === 0;
                          const value = row[col.id] || "";
                          const formattedValue = formatValue(value, col.id, rowIndex);
                          
                          return (
                            <td
                              key={col.id}
                              className="excel-data-cell"
                              style={{
                                textAlign: isFirstColumn ? 'left' : 'right',
                                fontWeight: '400',
                                color: '#333',
                                height: '26px',
                                padding: '2px 6px',
                                backgroundColor: isAlternateRow ? '#f0f8ff' : '#ffffff'
                              }}
                            >
                              {isFirstColumn ? (
                                <div className="excel-text">
                                  {formattedValue}
                                </div>
                              ) : (
                                <div className="excel-number">
                                  {formattedValue}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-60 text-gray-500 dark:text-gray-400">
              <FaFileExcel className="text-4xl mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                No {activeStatement} Data Available
              </p>
              <p className="text-sm">
                {selectedCompany.isBackendCompany
                  ? "No Excel file found or data could not be loaded"
                  : "Please select a backend company to view data"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* STYLES */}
      <style>{`
        .excel-wrapper {
          border: 1px solid #cfcfcf;
          overflow: auto;
          max-height: 70vh;
          background: #fff;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        .excel-table {
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 12.5px;
          min-width: 100%;
          color: #000;
          border-spacing: 0;
        }
        .excel-title-cell,
        .excel-header-cell,
        .excel-data-cell {
          border: 1px solid #d4d4d4;
          user-select: none;
          cursor: default;
          vertical-align: middle;
          box-sizing: border-box;
        }
        .excel-text { 
          text-align: left; 
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        .excel-number { 
          text-align: right; 
          font-family: 'Consolas', 'Monaco', monospace;
          white-space: nowrap;
          letter-spacing: 0.02em;
        }
        
        /* Hover effect for data rows only */
        .excel-data-row:hover .excel-data-cell {
          background-color: #f5f5f5 !important;
        }
        
        /* Fixed column widths */
        .excel-table td:first-child {
          width: 180px !important;
          min-width: 180px !important;
          max-width: 180px !important;
        }
        
        .excel-table td:not(:first-child) {
          width: 120px !important;
          min-width: 120px !important;
          max-width: 120px !important;
        }
        
        /* Title row specific styling */
        .excel-title-row .excel-title-cell {
          background-color: #ffffff;
          color: #000;
          border-bottom: 2px solid #999;
        }
        
        /* Header row specific styling */
        .excel-header-row .excel-header-cell {
          background-color: #f3f3f3;
          color: #000;
          border-bottom: 1px solid #b0b0b0;
        }
        
        /* Dark mode styles */
        @media (prefers-color-scheme: dark) {
          .excel-wrapper {
            background: #1f2937;
            border-color: #4b5563;
          }
          .excel-title-cell,
          .excel-header-cell,
          .excel-data-cell {
            border-color: #4b5563;
          }
          
          /* Title row in dark mode */
          .excel-title-row .excel-title-cell {
            background-color: #374151 !important;
            color: #f3f4f6 !important;
            border-bottom-color: #6b7280;
          }
          
          /* Header row in dark mode */
          .excel-header-row .excel-header-cell {
            background-color: #4b5563 !important;
            color: #f3f4f6 !important;
            border-bottom-color: #6b7280;
          }
          
          /* Data rows in dark mode */
          .excel-data-cell {
            background-color: #1f2937 !important;
            color: #f3f4f6 !important;
          }
          
          /* Alternating rows in dark mode */
          .excel-data-row:nth-child(odd) .excel-data-cell {
            background-color: #1f2937 !important;
          }
          
          .excel-data-row:nth-child(even) .excel-data-cell {
            background-color: #111827 !important;
          }
          
          /* Hover in dark mode */
          .excel-data-row:hover .excel-data-cell {
            background-color: #374151 !important;
          }
          
          .excel-text {
            color: #e5e7eb;
          }
          
          .excel-number {
            color: #93c5fd;
          }
        }
        
        /* Ensure no empty rows are displayed */
        tr:has(td:empty:only-child) {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default UserDataView;